import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useClientContext } from '../hooks/useClientContext';
import {
  useAssignedQuestionnaires,
  downloadQuestionnaireScorm,
  publishQuestionnaire,
  questionnaireKeys,
  fetchQuestionnaireLiveStatus,
  useQuestionnaireLiveStatus,
  QuestionnaireApiError,
  type AssignedQuestionnaire,
} from '../api/questionnaireApi';
import { CdnLiveStatus } from '../components/CdnLiveStatus';
import { QuestionnaireReadinessPanel } from '../components/QuestionnaireReadinessPanel';
import { QuestionnaireLifecycleBadge } from '../components/QuestionnaireLifecycleBadge';
import { WorkflowStatusBadge } from '../components/WorkflowStatusBadge';
import {
  AdminPageHeader,
  AdminStatusMessage,
  createAdminListStyles,
  useAdminNarrowViewport,
  type AdminListStyles,
} from '../admin-ui';
import { formatDateTime } from '../utils/formatDateTime';

type PendingAction = { id: string; type: 'publish' | 'download' };

/**
 * "<Content name> — <what is wrong>", falling back to the node when unnamed.
 * Deploy-side findings (branding assets) carry neither, so they read as the bare
 * reason.
 */
const describeTerminal = (terminal: { resourceName: string | null; nodeId: string; reason: string }): string => {
  const subject = terminal.resourceName ?? (terminal.nodeId ? `Node ${terminal.nodeId}` : null);
  return subject ? `${subject} — ${terminal.reason}` : terminal.reason;
};


/**
 * The published-package state for one questionnaire, mirroring the course
 * publish banner.
 *
 * Two deliberate differences from courses. The timestamp is when the PACKAGE was
 * deployed, read from its deploy stamp — a questionnaire has no author-api
 * publish row, and its authoring lifecycle belongs to wh-frontend — so the label
 * says "Published to CDN" rather than implying an authoring state. And there is no
 * "content is locked / edit published content" copy: nothing here is editable, so
 * that line would promise an action this app does not have.
 */
function QuestionnaireDeliveryState({
  questionnaire,
  refreshToken,
  styles,
}: {
  questionnaire: AssignedQuestionnaire;
  /** Changes after a publish so the row re-reads and the badge re-polls. */
  refreshToken: string;
  styles: AdminListStyles;
}) {
  const { data, isLoading } = useQuestionnaireLiveStatus(questionnaire, true);

  if (isLoading) {
    return <p style={{ ...styles.stateText, padding: 0, fontSize: '13px' }}>Checking delivery status…</p>;
  }
  if (!data?.deployed) {
    return (
      <p style={{ ...styles.stateText, padding: 0, fontSize: '13px' }}>
        Not published yet — publish to make it available to learners.
      </p>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <WorkflowStatusBadge state="published" />
        <span style={{ fontSize: '13px', color: '#6b7280' }}>
          Published to CDN {formatDateTime(data.deployedAt) ?? ''}
        </span>
      </div>
      {/*
        `publishedAt` is the CONTENT's publish time — the badge compares it against
        the revision the hosted package was baked from, to catch content that was
        published but never deployed. For a questionnaire that is hoot's
        `updatedAt`: an edit made in wh-frontend after the last publish then reads
        as stale delivery instead of passing as live. Passing the DEPLOY time here
        instead would be a category error — the hosted revision always predates the
        deploy that shipped it, so every healthy publish would flag.
        Undefined against an older hoot, which simply skips the check.
      */}
      <CdnLiveStatus
        packageId={questionnaire.id}
        accountSlug={questionnaire.accountSlug}
        pollToken={refreshToken}
        publishedAt={questionnaire.updatedAt ?? null}
        fetchStatus={fetchQuestionnaireLiveStatus}
        supersededBehavior="evaluate"
        // Same verdict as a course's missed deploy, different cause: nobody
        // publishes questionnaire CONTENT here, so a hosted revision behind the
        // authored one means it was edited in wh-frontend since the last publish.
        deployGapCopy={{
          title: '⚠ Edited since this package was published',
          detail: 'learners are still getting the previous version — publish again to ship the current one',
        }}
      />
    </div>
  );
}

/**
 * Download SCORM, disabled only when delivery status has LOADED and says nothing is
 * published for this account.
 *
 * The page already knows the answer — `QuestionnaireDeliveryState` renders "Not
 * published yet" from the same read — so offering the button anyway meant clicking to
 * be told something already on screen.
 *
 * Deliberately NOT disabled while the read is pending or after it failed: a disabled
 * button on a slow or broken status call would block a download that works, which is
 * worse than the round trip it saves. The server check stays the authority (the state
 * can change between render and click), so `NOT_DEPLOYED` is still handled by the
 * caller. A published-but-stale questionnaire also stays enabled: its shell points at
 * `current/` and is valid — the badge already warns that the content is behind.
 *
 * Shares the query key with `QuestionnaireDeliveryState`, so this costs no extra request.
 */
export function DownloadScormButton({
  questionnaire,
  pending,
  onDownload,
  styles,
}: {
  questionnaire: AssignedQuestionnaire;
  pending: boolean;
  onDownload: (questionnaire: AssignedQuestionnaire) => void;
  styles: AdminListStyles;
}) {
  const { data, isLoading, isError } = useQuestionnaireLiveStatus(questionnaire, true);
  const knownUnpublished = !isLoading && !isError && data !== undefined && !data.deployed;
  const disabled = pending || knownUnpublished;

  return (
    <button
      type="button"
      onClick={() => onDownload(questionnaire)}
      disabled={disabled}
      title={knownUnpublished ? 'Publish first to download the SCORM package' : undefined}
      style={{
        ...styles.primaryButton,
        ...(disabled ? styles.confirmDeleteButtonDisabled : {}),
      }}
    >
      {pending ? 'Downloading…' : 'Download SCORM'}
    </button>
  );
}

export function AssignedQuestionnairesPage() {
  const { clientContext } = useClientContext();
  const isNarrow = useAdminNarrowViewport();
  const styles = useMemo(() => createAdminListStyles({ narrow: isNarrow }), [isNarrow]);
  const { data: questionnaires, isLoading, isError } = useAssignedQuestionnaires(clientContext?.id);
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  /** Named terminal problems — blocking (with an error) or advisory (with a success). */
  const [actionWarnings, setActionWarnings] = useState<string[]>([]);
  /** Which row has its readiness panel open. Only one at a time — the read is not cheap. */
  const [openReadinessId, setOpenReadinessId] = useState<string | null>(null);
  /**
   * Bumped per questionnaire after a successful publish, so its delivery row
   * re-reads and the badge restarts its poll cycle against the NEW deploy rather
   * than reporting the previous one as live.
   */
  const [publishedTokens, setPublishedTokens] = useState<Record<string, string>>({});

  const handlePublish = useCallback(async (questionnaire: AssignedQuestionnaire) => {
    setActionError(null);
    setActionNotice(null);
    setActionWarnings([]);
    setPendingAction({ id: questionnaire.id, type: 'publish' });
    try {
      const { warnings } = await publishQuestionnaire(questionnaire);
      // Publishing produces no file, so without this the action looks like it
      // did nothing.
      setActionNotice(`"${questionnaire.title}" is published. You can now download its SCORM package.`);
      // Non-blocking terminal findings ride along with the success: the package
      // shipped, but the author still needs to know what may fail for learners.
      setActionWarnings(warnings.map((warning) => describeTerminal(warning)));
      const token = new Date().toISOString();
      setPublishedTokens((current) => ({ ...current, [questionnaire.id]: token }));
      await queryClient.invalidateQueries({ queryKey: [...questionnaireKeys.all, 'live', questionnaire.id] });
    } catch (error) {
      if (error instanceof QuestionnaireApiError && error.code === 'UNRESOLVED_TERMINALS') {
        setActionError(
          'This questionnaire assigns content that is not published for this account. Publish it first, then publish the questionnaire.',
        );
        // Naming the content is the whole point — an author cannot act on a node id.
        setActionWarnings((error.terminals ?? []).map((terminal) => describeTerminal(terminal)));
      } else {
        setActionError(error instanceof Error ? error.message : 'Failed to publish questionnaire.');
      }
    } finally {
      setPendingAction(null);
    }
  }, [queryClient]);

  const handleDownload = useCallback(async (questionnaire: AssignedQuestionnaire) => {
    setActionError(null);
    setActionNotice(null);
    setActionWarnings([]);
    setPendingAction({ id: questionnaire.id, type: 'download' });
    try {
      await downloadQuestionnaireScorm(questionnaire);
    } catch (error) {
      if (error instanceof QuestionnaireApiError && error.code === 'NOT_DEPLOYED') {
        setActionError('Publish this questionnaire before downloading its SCORM package.');
      } else {
        setActionError(error instanceof Error ? error.message : 'Failed to download SCORM package.');
      }
    } finally {
      setPendingAction(null);
    }
  }, []);

  const items = questionnaires ?? [];

  // Switching the top-bar account to Emtrain retracts the sidebar item (the nav is
  // gated on the account having assignments), but the route stays mounted — leaving
  // this page describing an account the author is no longer in. Placed after every
  // hook so the hook order is stable, and `replace` so Back does not return to a page
  // that immediately redirects again.
  //
  // Deliberately only the null-context case. The nav also hides for an account with
  // zero questionnaires, but redirecting on that would bounce the author out while the
  // count is still resolving — and the empty state below already reads correctly.
  if (clientContext === null) {
    return <Navigate to="/lessons" replace />;
  }

  return (
    <div style={styles.page}>
      <AdminPageHeader
        title="Questionnaires"
        description={clientContext && (
          <>
            Questionnaires cloned and assigned to {clientContext.name}. Download the SCORM package for use with
            this account's courses.
          </>
        )}
      />

      {actionError && (
        <AdminStatusMessage tone="error" role="alert">{actionError}</AdminStatusMessage>
      )}

      {actionNotice && (
        <AdminStatusMessage tone="success" role="status">{actionNotice}</AdminStatusMessage>
      )}

      {actionWarnings.length > 0 && (
        <AdminStatusMessage tone="warning" role={null}>
          <ul style={{ padding: 0, margin: 0, listStylePosition: 'inside' }}>
            {actionWarnings.map((warning) => (
              <li key={warning} style={{ fontSize: '13px' }}>{warning}</li>
            ))}
          </ul>
        </AdminStatusMessage>
      )}

      {isLoading ? (
        <section style={styles.panel}>
          <p style={styles.stateText}>Loading assigned questionnaires...</p>
        </section>
      ) : isError ? (
        <section style={styles.panel}>
          <p style={styles.stateText}>Unable to load assigned questionnaires. Please sign in again.</p>
        </section>
      ) : items.length === 0 ? (
        <section style={styles.panel}>
          <p style={styles.stateText}>No questionnaires are assigned to this account.</p>
        </section>
      ) : (
        <div style={styles.cardList}>
          {items.map((questionnaire) => (
            <div
              key={questionnaire.id}
              style={{
                ...styles.card,
                ...(openReadinessId === questionnaire.id ? { flexWrap: 'wrap' as const } : {}),
              }}
            >
              <div>
                <p style={styles.cardTitle}>{questionnaire.title}</p>
                <p style={styles.cardSubtitle}>Read-only — questionnaire authoring happens in wh-frontend</p>
                {questionnaire.lifecycle === 'publish' && (
                  <QuestionnaireDeliveryState
                    questionnaire={questionnaire}
                    refreshToken={publishedTokens[questionnaire.id] ?? 'initial'}
                    styles={styles}
                  />
                )}
              </div>
              {questionnaire.lifecycle === 'publish' ? (
                <div style={styles.cardActions}>
                  <button
                    type="button"
                    onClick={() => handlePublish(questionnaire)}
                    disabled={pendingAction?.id === questionnaire.id}
                    style={{
                      ...styles.primaryButton,
                      ...(pendingAction?.id === questionnaire.id && pendingAction.type === 'publish'
                        ? styles.confirmDeleteButtonDisabled
                        : {}),
                    }}
                  >
                    {pendingAction?.id === questionnaire.id && pendingAction.type === 'publish'
                      ? 'Publishing…'
                      : 'Publish'}
                  </button>
                  <DownloadScormButton
                    questionnaire={questionnaire}
                    pending={
                      pendingAction?.id === questionnaire.id && pendingAction.type === 'download'
                    }
                    onDownload={handleDownload}
                    styles={styles}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setOpenReadinessId((current) =>
                        current === questionnaire.id ? null : questionnaire.id,
                      )
                    }
                    aria-expanded={openReadinessId === questionnaire.id}
                    aria-label={
                      openReadinessId === questionnaire.id
                        ? 'Hide assigned resources'
                        : 'Show assigned resources'
                    }
                    title={
                      openReadinessId === questionnaire.id
                        ? 'Hide assigned resources'
                        : 'Show assigned resources'
                    }
                    // A disclosure control, not a third action: the two buttons beside it
                    // do something, this only reveals detail.
                    style={styles.disclosureButton}
                  >
                    <FontAwesomeIcon
                      icon={openReadinessId === questionnaire.id ? faChevronUp : faChevronDown}
                    />
                  </button>
                </div>
              ) : (
                <QuestionnaireLifecycleBadge lifecycle={questionnaire.lifecycle} />
              )}
              {openReadinessId === questionnaire.id && (
                <div style={{ flexBasis: '100%', width: '100%' }}>
                  <QuestionnaireReadinessPanel
                    questionnaire={questionnaire}
                    onRecheck={() => {
                      void queryClient.invalidateQueries({
                        queryKey: [...questionnaireKeys.all, 'readiness', questionnaire.id],
                      });
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
